import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import acceptedJobApi from '../../../APIs/jobs';
import { ReactComponent as CloseSVG } from '../../../assets/icons/cross.svg';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import UploadedPDFView from '../../../components/TechnicianJobs/ReviewAndSend/UploadedPDFView';
import {
  Button,
  FormContainer,
  NavigateBack,
  PageHeader,
  Tag,
  TextArea,
  Typography,
} from '../../../designComponents';
import { RootState } from '../../../store/selectors';
import { requestAPI } from '../../../utils';
import { getLatestReview } from '../../../utils/reviews';
import {
  ActionButton,
  CloseAdditionalWrapperStyled,
  Notes,
  PdfContainer,
  Wrapper,
} from './style';

type Props = {};

type InitialValuesType = {
  additionalNotes: string[];
};
const validationSchema = Yup.object().shape({
  additionalNotes: Yup.array()
    .of(Yup.string().required('Note is required'))
    .required('At least one note is required'),
});
const ViewReviews = (_props: Props) => {
  const formikRef = useRef<FormikProps<InitialValuesType>>(null);
  const [loading, setLoading] = useState(false);
  const [rejectModalStatus, setRejectModalStatus] = useState(false);
  const [acceptModalStatus, setAcceptModalStatus] = useState(false);
  const { jobId } = useParams();
  const [changeButtonLabel, setChangeButtonLabel] = useState<
  'ACCEPT' | 'REJECT'
  >('ACCEPT');
  const reviewDetails = useSelector(
    (state: RootState) => state.singleJobProcessReview,
  );
  const jobReviewDetails = async (id: string) => {
    try {
      await requestAPI(acceptedJobApi.jobProcessReviewDetails(id));
    } catch (error) {
      console.error(error);
    }
  };

  const reviews = reviewDetails?.review || [];

  const latestReview = getLatestReview(reviews);

  useEffect(() => {
    if (jobId) {
      jobReviewDetails(jobId);
    }
  }, [jobId]);

  const handleAddAdditionalRequirement = () => {
    if (formikRef && formikRef.current) {
      const additionalNotes = formikRef.current.values.additionalNotes || [];
      const newNote = '';
      formikRef.current.setFieldValue('additionalNotes', [
        ...additionalNotes,
        newNote,
      ]);
    }
  };

  const handleRemoveAdditionalRequirement = (idx: number) => {
    if (formikRef && formikRef.current) {
      const additionalReq = formikRef.current.values.additionalNotes;

      additionalReq.splice(idx, 1);

      formikRef.current.setFieldValue('additionalNotes', [...additionalReq]);
    }
  };

  const handleAcceptReview = async (values: { additionalNotes: string[] }) => {
    if (!latestReview) {
      return;
    }
    try {
      setLoading(true);
      await requestAPI(
        acceptedJobApi.acceptReview(
          {
            review: values.additionalNotes,
            reviewId: latestReview?._id || '',
            reviewStatus: changeButtonLabel,
          },
          jobId || '',
        ),
      );
      if (changeButtonLabel === 'ACCEPT') {
        toast.success('Review accepted successfully');
      } else {
        toast.success('Review rejected successfully');
      }
      if (jobId) {
        jobReviewDetails(jobId);
      }
      setLoading(false);
      await requestAPI(acceptedJobApi.jobProcessReviewDetails(jobId || ''));
    } catch (error) {
      console.error(error);
    }
  };
  const onCloseModalReject = () => setRejectModalStatus(false);
  const onCloseModalAccept = () => setAcceptModalStatus(false);

  return (
    <Wrapper>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'sticky',
          top: '60px',
          zIndex: '999',
          paddingTop: '5px',
          background: '#fff',
        }}
      >
        <NavigateBack />
      </div>
      <div className="contents">
        <PageHeader pageTitle="REVIEW" showSearch={false} />
        <PdfContainer>
          {latestReview && <UploadedPDFView review={latestReview} />}
        </PdfContainer>

        {latestReview?.reviewStatus === 'REJECT' ||
        latestReview?.reviewStatus === 'ACCEPT' ? (
          <Tag
            type={
              latestReview?.reviewStatus === 'REJECT' ? 'danger' : 'success'
            }
          >
            {'PDF' + ' ' + latestReview?.reviewStatus + 'ED'}
          </Tag>
          ) : (
          <Formik
            onSubmit={handleAcceptReview}
            innerRef={formikRef}
            initialValues={{ additionalNotes: [''] }}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, errors }) => (
              <Form>
                <Notes>
                  <Typography className="text">Add Your Reviews</Typography>
                  {values.additionalNotes.map((note, index) => {
                    return (
                      <div key={index} style={{ maxWidth: '560px' }}>
                        {index !== 0 && (
                          <CloseAdditionalWrapperStyled>
                            <CloseSVG
                              onClick={() =>
                                handleRemoveAdditionalRequirement(index)
                              }
                            />
                          </CloseAdditionalWrapperStyled>
                        )}

                        <FormContainer
                          label=""
                          error={
                            errors.additionalNotes
                              ? errors.additionalNotes[index]
                              : ''
                          }
                        >
                          <TextArea
                            key={index}
                            placeholder="Please add notes.."
                            className="note-input"
                            value={note || ''}
                            onChange={(
                              e: React.ChangeEvent<HTMLTextAreaElement>,
                            ) => {
                              const { value } = e.target;
                              const updatedValues = [...values.additionalNotes];
                              updatedValues[index] = value;

                              setFieldValue('additionalNotes', updatedValues);
                            }}
                            showCount
                            maxLength={800}
                          />
                        </FormContainer>
                      </div>
                    );
                  })}

                  <Button
                    variant="gray"
                    type="button"
                    onClick={handleAddAdditionalRequirement}
                  >
                    Add More Notes
                  </Button>
                </Notes>

                <>
                  <ActionButton>
                    <Button
                      variant="secondary"
                      isLoading={changeButtonLabel === 'ACCEPT' && loading}
                      onClick={async () => {
                        if (formikRef.current) {
                          await formikRef.current.validateForm();

                          if (formikRef.current.errors.additionalNotes) {
                            return;
                          }
                          setAcceptModalStatus(true);
                        }
                      }}
                    >
                      Accept PDF
                    </Button>
                    <Button
                      variant="danger"
                      isLoading={changeButtonLabel === 'REJECT' && loading}
                      onClick={async () => {
                        if (formikRef.current) {
                          await formikRef.current.validateForm();

                          if (formikRef.current.errors.additionalNotes) {
                            return;
                          }
                          setRejectModalStatus(true);
                        }
                      }}
                    >
                      Reject PDF
                    </Button>
                  </ActionButton>
                </>
              </Form>
            )}
          </Formik>
          )}
      </div>
      <ConfirmationModal
        message="Are you sure want to accpet the pdf?"
        isOpen={acceptModalStatus}
        onClose={onCloseModalAccept}
        title="Review"
        okText="Send"
        onOk={() => {
          if (formikRef.current) {
            setChangeButtonLabel('ACCEPT');
            formikRef.current.submitForm();
            setAcceptModalStatus(false);
          }
        }}
      />
      <ConfirmationModal
        message={'Are you sure want to reject the pdf? This pfd will re-sent to the technician for enhancement. '}
        isOpen={rejectModalStatus}
        onClose={onCloseModalReject}
        title="Review"
        onOk={() => {
          if (formikRef.current) {
            setChangeButtonLabel('REJECT');
            formikRef.current.submitForm();
            setRejectModalStatus(false);
          }
        }}
        okText="Send"
      />
    </Wrapper>
  );
};

export default ViewReviews;
